import type { LoanRevenue } from '@/components/banker-recon/types';
import { isPaidToLendwise, leFeeSlug, APPLICATION_FEE_LINE_TYPE, LOAN_DISCOUNT_SELLER_PAID } from './leFeeCanonical';
import type {
  FeePayer,
  ReconCapturedFee,
  ReconFeeCapture,
  ReconFeeSource
} from '../../../supabase/functions/_shared/banker-recon-fee-types';

export { readReconFeeAccounting, RECON_FEE_ACCOUNTING_FLAG } from '../../../supabase/functions/_shared/banker-recon-fee-types';
export type { ReconFeeAccountingSnapshot, ReconFeeCapture, ReconFeeSource } from '../../../supabase/functions/_shared/banker-recon-fee-types';

const cents = (amount: number) => Math.round(amount * 100);
const rowCents = (row: LoanRevenue) => cents(row.amount + (row.warehouse_retained_amount || 0));
const closingPayers = ['borrower', 'seller', 'lender'] as const;

function closingCents(fee: ReconCapturedFee, payer: FeePayer): number {
  // Preserve the existing borrower own-funds contract; PAC also contains financing.
  return cents(payer === 'borrower'
    ? fee.borrowerOwnFunds ?? 0
    : fee.payments[payer]?.atClosing ?? 0);
}

export function capturedFeeOwner(fee: ReconCapturedFee): string | null {
  if (fee.line === '801c' && isPaidToLendwise(fee)) {
    return 'processing_fee';
  }
  if (fee.line === '801d' && isPaidToLendwise(fee)) {
    return 'underwriting_fee';
  }
  if (fee.line === '801b') {
    return APPLICATION_FEE_LINE_TYPE;
  }
  if (fee.line === '802e' && isPaidToLendwise(fee) && (fee.payments.seller?.total ?? 0) > 0) {
    return LOAN_DISCOUNT_SELLER_PAID;
  }
  return null;
}

export function feePortionId(fee: ReconCapturedFee, payer: FeePayer): string {
  return `recon_fee_${fee.line}_${payer}_pac`;
}

function sourceFor(fee: ReconCapturedFee, payer: FeePayer): ReconFeeSource {
  return {
    sourceFeeKey: fee.sourceFeeKey,
    portionKey: `${payer}:PAC`,
    payer,
    timing: 'PAC',
    owner: payer === 'lender' ? 'encompass' : 'manual'
  };
}

export interface FeeLedgerResult {
  rows: LoanRevenue[];
  issues: string[];
}

/**
 * One allocation pass for load, edits, save and dropdown. Doc Order decisions
 * own their portions; Encompass owns amounts. An uncertain legacy match is
 * preserved for review rather than guessed or duplicated.
 */
export function reconcileFeePaymentLedger(current: LoanRevenue[], capture: ReconFeeCapture): FeeLedgerResult {
  if (capture.status !== 'complete') {
    return { rows: current, issues: capture.issues.length ? capture.issues : ['Fee capture must be refreshed'] };
  }
  const issues: string[] = [];
  const consumed = new Set<LoanRevenue>();
  const replacement: LoanRevenue[] = [];
  const legacyMatches = new Map<string, ReconCapturedFee[]>();
  for (const fee of capture.fees) {
    for (const name of new Set([fee.name, fee.legacyName])) {
      const key = `section_a_${leFeeSlug(name)}`;
      legacyMatches.set(key, [...(legacyMatches.get(key) || []), fee]);
    }
  }

  for (const fee of capture.fees) {
    const ownId = capturedFeeOwner(fee);
    const related = current.filter(row => row.fee_source?.sourceFeeKey === fee.sourceFeeKey);
    const legacy = current.filter(row => !row.fee_source
      && legacyMatches.get(row.id)?.some(candidate => candidate.sourceFeeKey === fee.sourceFeeKey));
    const ambiguous = legacy.some(row => new Set(legacyMatches.get(row.id)?.map(candidate => candidate.sourceFeeKey)).size !== 1);
    const manual = related.filter(row => row.fee_source?.owner !== 'doc_order');
    const docRows = related.filter(row => row.fee_source?.owner === 'doc_order');
    const lenderAmount = closingCents(fee, 'lender');
    const ownerRow = ownId ? current.find(row => row.id === ownId) : undefined;
    const hasLenderHistory = manual.some(row => row.fee_source?.payer === 'lender');

    // Leave non-lender legacy behavior untouched unless a selected fee overlaps a Doc Order.
    if (!lenderAmount && !hasLenderHistory && !manual.length && !docRows.length && !ownerRow) {
      continue;
    }
    if (ambiguous || legacy.length > 1) {
      issues.push(`Resolve duplicate or ambiguous saved entries for ${fee.name} (${fee.line})`);
      continue;
    }

    const claimed: Partial<Record<FeePayer, number>> = {};
    for (const row of docRows) {
      const source = row.fee_source!;
      const available = cents(fee.payments[source.payer]?.[source.timing === 'PAC' ? 'atClosing' : 'outsideClosing'] ?? 0);
      if (rowCents(row) > available) {
        issues.push(`Doc Order and Encompass disagree for ${fee.name} (${fee.line})`);
      }
      if (source.timing === 'PAC') {
        claimed[source.payer] = (claimed[source.payer] || 0) + rowCents(row);
      }
    }
    for (const payer of closingPayers) {
      if ((claimed[payer] || 0) > cents(fee.payments[payer]?.atClosing ?? 0)) {
        issues.push(`Doc Order portions exceed ${fee.name} (${fee.line}) ${payer} closing amount`);
      }
    }

    const total = closingPayers.reduce((sum, payer) => sum + closingCents(fee, payer), 0);
    const legacyTotal = total - lenderAmount;
    const legacyRow = legacy[0];
    const legacyOwnsLender = !!legacyRow && rowCents(legacyRow) === total;
    if (legacyRow && rowCents(legacyRow) !== total && rowCents(legacyRow) !== legacyTotal) {
      issues.push(`Review the saved amount for ${fee.name} (${fee.line}) against Encompass`);
      continue;
    }
    if (ownId) {
      // Keep the established scalar/auto owner. No second lender-fee row.
      const docClaim = closingPayers.reduce((sum, payer) => sum + (claimed[payer] || 0), 0);
      const ownedTotal = ['801c', '801d', '802e'].includes(fee.line)
        ? cents(fee.payments.borrower?.total ?? 0) + cents(fee.payments.seller?.total ?? 0) + lenderAmount
        : total;
      replacement.push({
        ...(ownerRow || { id: ownId, description: fee.name }),
        amount: Math.max(0, ownedTotal - docClaim) / 100,
        warehouse_retained_amount: 0,
        nets_to_warehouse_advance: true,
        reimbursable: false
      });
      if (ownerRow) {
        consumed.add(ownerRow);
      }
      for (const row of [...legacy, ...manual]) {
        consumed.add(row);
      }
      continue;
    }

    for (const row of [...legacy, ...manual]) {
      consumed.add(row);
    }
    for (const payer of closingPayers) {
      const prior = manual.filter(row => row.fee_source?.payer === payer && row.fee_source.timing === 'PAC');
      if (prior.length > 1) {
        issues.push(`Resolve duplicate ${payer} allocations for ${fee.name} (${fee.line})`);
        replacement.push(...prior);
        continue;
      }
      if (payer !== 'lender' && !legacyRow && !prior.length) {
        continue;
      }
      const amount = Math.max(0, closingCents(fee, payer) - (claimed[payer] || 0));
      if (!amount) {
        continue;
      }
      replacement.push({
        id: feePortionId(fee, payer),
        description: fee.name,
        amount: amount / 100,
        reimbursable: prior[0]?.reimbursable ?? ((payer !== 'lender' || legacyOwnsLender) && legacyRow?.reimbursable === true),
        warehouse_retained_amount: 0,
        nets_to_warehouse_advance: true,
        fee_source: sourceFor(fee, payer)
      });
    }
  }

  for (const row of current) {
    if (row.fee_source && !capture.fees.some(fee => fee.sourceFeeKey === row.fee_source?.sourceFeeKey)) {
      issues.push(`Review the unavailable source for ${row.description}`);
    }
  }
  return { rows: [...current.filter(row => !consumed.has(row)), ...replacement], issues: [...new Set(issues)] };
}

/** Manual selection adds only closing portions not already represented. */
export function addCapturedFee(current: LoanRevenue[], fee: ReconCapturedFee, capture: ReconFeeCapture): FeeLedgerResult {
  const rows = [...current];
  for (const payer of ['borrower', 'seller'] as const) {
    if (rows.some(row => row.fee_source?.sourceFeeKey === fee.sourceFeeKey
      && row.fee_source.payer === payer && row.fee_source.owner !== 'doc_order')) {
      continue;
    }
    if (closingCents(fee, payer) > 0) {
      rows.push({
        id: feePortionId(fee, payer),
        description: fee.name,
        amount: closingCents(fee, payer) / 100,
        reimbursable: false,
        nets_to_warehouse_advance: true,
        warehouse_retained_amount: 0,
        fee_source: sourceFor(fee, payer)
      });
    }
  }
  return reconcileFeePaymentLedger(rows, capture);
}

export function availableCapturedFeeAmount(current: LoanRevenue[], fee: ReconCapturedFee): number {
  if (capturedFeeOwner(fee) || current.some(row => !row.fee_source
    && [fee.name, fee.legacyName].some(name => row.id === `section_a_${leFeeSlug(name)}`))) {
    return 0;
  }
  return ['borrower', 'seller'].reduce((sum, value) => {
    const payer = value as FeePayer;
    const represented = current.filter(row => row.fee_source?.sourceFeeKey === fee.sourceFeeKey
      && row.fee_source.payer === payer && row.fee_source.timing === 'PAC').reduce((total, row) => total + rowCents(row), 0);
    return sum + Math.max(0, closingCents(fee, payer) - represented);
  }, 0) / 100;
}
